import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, CircularProgress, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import axios from 'axios';
import CustomButton from "../../common/CustomButton";
import CustomTextField from "../../common/CustomTextField";
import CustomTextarea from "../../common/CustomTextarea";
import CustomAutocomplete from "../../common/CustomAutoComplete";
import { READ_EXPENSE_VOUCHER, UPDATE_EXPENSE_VOUCHER } from '../../../services/apiRoutes';
import CustomStyle from "../../common/CustomStyle";
import CustomPrefixSuffixField from "../../common/CustomPrefixSuffixField";
import { t } from "../../../constants/translationHelper";

const ValidationSchema = Yup.object().shape({
    date: Yup.date().required('Date is required'),
    voucherNo: Yup.string().required('Voucher No is required'),
    expensesLedgerId: Yup.number().required('Expenses Ledger is required'),
    amount: Yup.number().required('Amount is required'),
    currencyId: Yup.number().required('Currency is required'),
    baseCurrencyId: Yup.number().required('Base Currency is required'),
    conversionRate: Yup.number().required('Conversion Rate is required'),
    amountInBaseCurrency: Yup.number().required('Amount in Base Currency is required'),
    partyLedgerId: Yup.number().required('Party Ledger is required'),

});


const UpdateExpenseVoucher = ({ handleClose, setMessage, fetchData, commercialInvoiceList, ExpenseVoucherId, partyLedger, companyCurrencyData, currencyList, expenseLedger }) => {
    const [data, setData] = useState(null);
    const [currencySymbol, setCurrencySymbol] = useState(null);
    const [baseCurrencySymbol, setBaseCurrencySymbol] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!ExpenseVoucherId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_EXPENSE_VOUCHER(ExpenseVoucherId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                const currencySymbol = response?.data?.expenseVoucher?.currency?.currency_symbol || '';
                const baseCurrencySym = response?.data?.expenseVoucher?.baseCurrency?.currency_symbol || '';
                setBaseCurrencySymbol(baseCurrencySym);
                setCurrencySymbol(currencySymbol);
                setData(response.data.expenseVoucher);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [ExpenseVoucherId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            const response = await axios.put(UPDATE_EXPENSE_VOUCHER(ExpenseVoucherId), values, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "Expense Voucher updated successfully",
                    type: "success",
                });
                fetchData();
                setTimeout(() => {
                    handleClose();
                }, 500);
            } else {
                alert('Something went wrong');
            }
        } catch (error) {
            alert('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={{
                    date: data?.date || '',
                    dueDate: data?.dueDate || '',
                    voucherNo: data?.voucherNo || '',
                    expensesLedgerId: data?.expensesLedgerId || '',
                    partyLedgerId: data?.partyLedgerId || '',
                    amount: data?.amount || '',
                    currencyId: data?.currencyId || '',
                    baseCurrencyId: data?.baseCurrencyId || '',
                    conversionRate: data?.conversionRate || '',
                    amountInBaseCurrency: data?.amountInBaseCurrency || '',
                    note: data?.note || '',
                    commercialInvoiceId: Number(data?.commercialInvoiceId) || '',
                }}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={4} md={4}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    {t("Update Expense Voucher")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />

                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container spacing={2} sx={{ px: "5%", paddingTop: "5px" }}>
                            <Grid item xs={12} md={3}>
                                <CustomAutocomplete
                                    options={commercialInvoiceList}
                                    fieldLabel="Commercial Invoice"
                                    getOptionLabel={(option) => option.title}
                                    value={commercialInvoiceList.find((option) => option.id === values.commercialInvoiceId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('commercialInvoiceId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Commercial Invoice"
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                <CustomTextField
                                    name="date"
                                    fieldLabel="Date"
                                    required={true}
                                    type="date"
                                    value={values.date}
                                    onChange={handleChange}
                                    error={touched.date && !!errors.date}
                                    helperText={touched.date && errors.date}
                                    placeholder="Enter Date"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                <CustomTextField
                                    required={true}
                                    name="voucherNo"
                                    fieldLabel="Expense Voucher No"
                                    value={values.voucherNo}
                                    onChange={handleChange}
                                    error={touched.voucherNo && !!errors.voucherNo}
                                    helperText={touched.voucherNo && errors.voucherNo}
                                    placeholder="Enter Voucher No"
                                    sx={CustomStyle.readOnly}
                                    readOnly={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                <CustomTextField
                                    name="dueDate"
                                    fieldLabel="due Date"
                                    type="date"
                                    value={values.dueDate}
                                    onChange={handleChange}
                                    placeholder="Enter Date"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                <CustomAutocomplete
                                    options={expenseLedger}
                                    fieldLabel="Select Expense Ledger"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={expenseLedger.find((option) => option.id === values.expensesLedgerId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('expensesLedgerId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Expenses Ledger"
                                    error={touched.expensesLedgerId && !!errors.expensesLedgerId}
                                    helperText={touched.expensesLedgerId && errors.expensesLedgerId}

                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={partyLedger}
                                    fieldLabel="Select Party Ledger"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={partyLedger.find((option) => option.id === values.partyLedgerId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('partyLedgerId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Party Ledger"
                                    error={touched.partyLedgerId && !!errors.partyLedgerId}
                                    helperText={touched.partyLedgerId && errors.partyLedgerId}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                                <CustomAutocomplete
                                    options={companyCurrencyData}
                                    fieldLabel="Currency"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={companyCurrencyData.find((option) => option.id === values.currencyId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('currencyId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Currency"
                                    error={touched.currencyId && !!errors.currencyId}
                                    helperText={touched.currencyId && errors.currencyId}
                                />
                            </Grid>


                            <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
                                <CustomPrefixSuffixField
                                    prefix={
                                        values.currencyId
                                            ? currencyList.find(option => option.id === values.currencyId)?.currencySymbol
                                            : null
                                    }
                                    required={true}
                                    name="amount"
                                    fieldLabel="Amount"
                                    type="number"
                                    value={values.amount}
                                    onChange={(e) => {
                                        const amount = e.target.value;
                                        setFieldValue('amount', amount);
                                        setFieldValue('amountInBaseCurrency', amount * values.conversionRate || 0);
                                    }}
                                    error={touched.amount && !!errors.amount}
                                    helperText={touched.amount && errors.amount}
                                    placeholder="Enter Amount"
                                />
                            </Grid>


                            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                <CustomAutocomplete
                                    options={currencyList}
                                    fieldLabel="Base Currency"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={currencyList.find((option) => option.id === values.baseCurrencyId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('baseCurrencyId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Base Currency"
                                    sx={CustomStyle.readOnly}
                                    readOnly={true}
                                    error={touched.baseCurrencyId && !!errors.baseCurrencyId}
                                    helperText={touched.baseCurrencyId && errors.baseCurrencyId}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                <CustomPrefixSuffixField
                                    required={true}
                                    prefix={baseCurrencySymbol}
                                    tagLine="(In Base)"
                                    name="conversionRate"
                                    fieldLabel="Conversion Rate"
                                    type="number"
                                    value={values.conversionRate}
                                    onChange={(e) => {
                                        const conversionRate = e.target.value;
                                        setFieldValue('conversionRate', conversionRate);
                                        setFieldValue('amountInBaseCurrency', values.amount * conversionRate || 0);
                                    }}
                                    error={touched.conversionRate && !!errors.conversionRate}
                                    helperText={touched.conversionRate && errors.conversionRate}
                                    placeholder="Enter Conversion Rate"
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                <CustomPrefixSuffixField
                                    required={true}
                                    prefix={baseCurrencySymbol}
                                    tagLine="(In Base Currency)"
                                    name="amountInBaseCurrency"
                                    fieldLabel="Base Amount"
                                    type="number"
                                    value={values.amountInBaseCurrency}
                                    onChange={handleChange}
                                    error={touched.amountInBaseCurrency && !!errors.amountInBaseCurrency}
                                    helperText={touched.amountInBaseCurrency && errors.amountInBaseCurrency}
                                    placeholder="Enter Amount in Base Currency"
                                    readOnly={true}
                                    sx={CustomStyle.readOnly}

                                />
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <CustomTextarea
                                    name="note"
                                    fieldLabel="Note"
                                    value={values.note}
                                    onChange={handleChange}
                                    placeholder="Enter Note"
                                    sx={{
                                        height: "80px !important"
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UpdateExpenseVoucher;
